'use client'

import {
  useCallback,
  useRef,
  useState,
  type PointerEvent,
  type MouseEvent,
  type DragEvent,
} from 'react'

const DRAG_THRESHOLD_PX = 4

/**
 * Pointer drag → horizontal `scrollLeft`.
 * Works for mouse and touch (including when children are buttons/links);
 * after a real drag, the following click is suppressed.
 */
export function useHorizontalDragScroll() {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{
    pointerId: number
    startX: number
    scrollLeft: number
  } | null>(null)
  const suppressClickRef = useRef(false)
  const [dragging, setDragging] = useState(false)

  const onPointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    // Pen uses native scroll; mouse + touch get drag-scroll.
    if (event.pointerType === 'pen') return

    const el = scrollerRef.current
    if (!el) return

    // Stop native image / text drag from stealing the gesture.
    event.preventDefault()

    suppressClickRef.current = false
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: el.scrollLeft,
    }
    el.setPointerCapture(event.pointerId)
    setDragging(true)
  }, [])

  const onDragStart = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }, [])

  const onPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    const el = scrollerRef.current
    if (!drag || !el || event.pointerId !== drag.pointerId) return

    const dx = event.clientX - drag.startX
    if (Math.abs(dx) > DRAG_THRESHOLD_PX) suppressClickRef.current = true
    el.scrollLeft = drag.scrollLeft - dx
  }, [])

  const endDrag = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    const el = scrollerRef.current
    if (!drag || event.pointerId !== drag.pointerId) return

    if (el?.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId)
    }
    dragRef.current = null
    setDragging(false)
  }, [])

  const onClickCapture = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (!suppressClickRef.current) return
    event.preventDefault()
    event.stopPropagation()
    suppressClickRef.current = false
  }, [])

  return {
    scrollerRef,
    dragging,
    dragHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onClickCapture,
      onDragStart,
    },
  }
}
